/* eslint-disable max-lines */
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import { RootState } from '../store';
import TaskApi from '../../apis/task';
import { AddTasksListItemParams, AddTasksListParams, AssignTaskListItemParams, BoardObject } from '../../params/redux/task';
import { createAsyncThunk, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { resettingTaskLoader, settingAddingTask, settingSelectedBoardTaskDetail } from '../slices/task';

interface ClientTaskResponse extends Record<string, unknown> {
    success: 0 | 1;
    board?: BoardObject;
    board_list_items?: BoardObject;
    token: string;
}

export const fetchSingleTaskItemThunk = createAsyncThunk(
    'task/fetch-item',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.fetchSingleTaskItemDetails(state.app.token, id);
            if (response?.success === 1) {
                return response?.item;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const fetchClientBoardDetails = createAsyncThunk(
    'task/fetch-client-board',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: ClientTaskResponse = await TaskApi.fetchClientBoardDetails(state.app.token, client_id) as ClientTaskResponse;
            if (response?.success === 1) {
                const lists = response?.board?.lists;
                const { selectedBoardTask } = state.task;
                if (selectedBoardTask && lists) {
                    let foundTaskItem = null;
                    for (const list of lists) {
                        foundTaskItem = list.items.find((item) => item.id === selectedBoardTask.id);
                        // eslint-disable-next-line max-depth
                        if (foundTaskItem) break;
                    }
                    if (foundTaskItem) {
                        dispatch(settingSelectedBoardTaskDetail(foundTaskItem));
                    } else {
                        dispatch(settingSelectedBoardTaskDetail(null));
                    }
                }
                return response.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const fetchClientTaskThunk = createAsyncThunk(
    'task/fetch-client',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: ClientTaskResponse = await TaskApi.fetchClientTask(state.app.token, client_id) as ClientTaskResponse;
            if (response?.success === 1) {
                const lists = response?.board?.lists;
                const { selectedBoardTask } = state.task;
                if (selectedBoardTask && lists) {
                    let foundTaskItem = null;
                    for (const list of lists) {
                        foundTaskItem = list.items.find((item) => item.id === selectedBoardTask.id);
                        // eslint-disable-next-line max-depth
                        if (foundTaskItem) break;
                    }
                    if (foundTaskItem) {
                        dispatch(settingSelectedBoardTaskDetail(foundTaskItem));
                    } else {
                        dispatch(settingSelectedBoardTaskDetail(null));
                    }
                }
                return response.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const fetchAssignedTaskThunk = createAsyncThunk(
    'task/fetch-assigned',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: ClientTaskResponse = await TaskApi.fetchAssignedListItems(state.app.token) as ClientTaskResponse;
            if (response?.success === 1) {
                return response?.board_list_items;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

const fetchTaskHandler = async (dispatch: ThunkDispatch<unknown, unknown, UnknownAction>, state: RootState, client_id: number) => {
    try {
        if (state.app.currentUser?.role === 'client') {
            await dispatch(fetchClientBoardDetails(client_id)).unwrap();
        } else {
            await dispatch(fetchClientTaskThunk(client_id)).unwrap();
        }
    } catch (e) {
        //
    }
};

export const assignTaskListItemThunk = createAsyncThunk(
    'task/assign-user',
    async (data: AssignTaskListItemParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.assignTaskListItem(state.app.token, data);
            if (response?.success === 1) {
                fetchTaskHandler(dispatch, state, data.client_id);
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const removeAssignTaskListItemThunk = createAsyncThunk(
    'task/remove-user',
    async (data: AssignTaskListItemParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.removeAssignedListItem(state.app.token, data);
            if (response?.success === 1) {
                fetchTaskHandler(dispatch, state, data.client_id);
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const createTaskListThunk = createAsyncThunk(
    'task/create',
    async (data: AddTasksListParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.createTaskList(state.app.token, data);
            if (response?.success === 1) {
                fetchTaskHandler(dispatch, state, data.client_id);
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const createTaskListItemThunk = createAsyncThunk(
    'task/create-item',
    async ({ data, client_id }:{data: AddTasksListItemParams, client_id: number}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const dataForApi = {
                ...data,
                client_id
            };
            const response = await TaskApi.createTaskListItem(state.app.token, dataForApi);
            if (response?.success === 1) {
                fetchTaskHandler(dispatch, state, client_id);
                dispatch(settingAddingTask(false));
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const dragTaskListItemThunk = createAsyncThunk(
    'task/drag-update',
    async (data: { list_id: number, item_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.dragTaskListItem(state.app.token, data.item_id, data.list_id);
            if (response?.success === 1) {
                fetchTaskHandler(dispatch, state, data.client_id);
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const createTaskListItemCommentThunk = createAsyncThunk(
    'task/create-comment',
    async (data: { id: number, comment: string, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.createTaskListItemComment(state.app.token, data.id, data.comment);
            if (response?.success === 1) {
                if (state.task.selectedBoardTask) {
                    await dispatch(fetchSingleTaskItemThunk(state.task.selectedBoardTask?.id as number)).unwrap();
                }
                fetchTaskHandler(dispatch, state, data.client_id);
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const addTaskListItemfileThunk = createAsyncThunk(
    'task/add-file',
    async (data: { id: number, file: string, filename: string, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.addTaskListItemfile(state.app.token, data.id, data.file, data.filename);
            if (response?.success === 1) {
                if (state.task.selectedBoardTask) {
                    await dispatch(fetchSingleTaskItemThunk(state.task.selectedBoardTask?.id as number)).unwrap();
                }
                fetchTaskHandler(dispatch, state, data.client_id);
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const deleteTaskListItemThunk = createAsyncThunk(
    'task/delete-item',
    async ({ id, client_id }: {id: number, client_id: number}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.deleteTaskItem(state.app.token, id);
            if (response?.success === 1) {
                if (state.task.selectedBoardTask) {
                    await dispatch(fetchSingleTaskItemThunk(state.task.selectedBoardTask?.id as number)).unwrap();
                }
                fetchTaskHandler(dispatch, state, client_id);
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const markTaskListItemCompleteThunk = createAsyncThunk(
    'task/mark-complete',
    async (data: { id: number, is_completed: 0 | 1, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.markTaskItemComplete(state.app.token, data.id, data.is_completed);
            if (response?.success === 1) {
                if (state.task.selectedBoardTask) {
                    await dispatch(fetchSingleTaskItemThunk(state.task.selectedBoardTask?.id as number)).unwrap();
                }
                fetchTaskHandler(dispatch, state, data.client_id);
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const editTaskListItemThunk = createAsyncThunk(
    'task/edit',
    async ({ client_id, data }:{client_id: number, data: AddTasksListItemParams}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.editTaskListItem(state.app.token, data);
            if (response?.success === 1) {
                if (state.task.selectedBoardTask) {
                    await dispatch(fetchSingleTaskItemThunk(state.task.selectedBoardTask?.id as number)).unwrap();
                }
                fetchTaskHandler(dispatch, state, client_id);
                return response?.board;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const fetchDueTasksListThunk = createAsyncThunk(
    'task/due',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.fetchDueTaskList(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.due_tasks;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const editBoardListThunk = createAsyncThunk(
    'task/list-edit',
    async ({ title, client_id }:{title: string, client_id: number}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.editBoardList(state.app.token, state.task.selectedBoardList?.id || 0, title, client_id);
            if (response?.success === 1) {
                fetchTaskHandler(dispatch, state, client_id);
                return response?.due_tasks;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const deleteBoardListThunk = createAsyncThunk(
    'task/list-delete',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.deleteBoardList(state.app.token, state.task.selectedBoardList?.id || 0, client_id);
            if (response?.success === 1) {
                fetchTaskHandler(dispatch, state, client_id);
                return response?.due_tasks;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);

export const fetchAllUrgentTasksThunk = createAsyncThunk(
    'task/fetch-all',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await TaskApi.fetchAllUrgentDueTask(state.app.token);
            if (response?.success === 1) {
                return response?.urgent_due_tasks;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingTaskLoader());
        }
    }
);